import { Skeleton } from "antd";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import LayoutDownload from "../../components/Layout/LayoutDownload";
import ExtensionPluginConfirm from "../../components/RecorbDowload/ExtensionPluginConfirm";
import ExtensionBoxDownload, {
  ExtensionBoxDownloadRef,
} from "../../components/RecorbDowload/ExtentionBoxDownload";
import { useExtensionForm } from "../../components/RecorbDowload/hooks/use-extension-form";
import RecorbBoxDownload, {
  RecorbBoxDownloadRef,
} from "../../components/RecorbDowload/RecorbBoxDownload";
import RecorbDownloadResult from "../../components/RecorbDowload/RecorbDownloadResult";
import { useAppDownloadDetail } from "../../hooks/useAppDownloadDetail";
import { useAppDownloadDetailVersion } from "../../hooks/useAppDownloadDetailVersion";
import dicomLogo from "../../images/dicom-plugin-logo.png";
import inforIcon from "../../images/infor-icon.png";
import downloadIcon from "../../images/install-desktop.png";
import { api } from "../../utils/api";
import { DATE_FORMAT } from "../../utils/constants/common";
import { formatDate } from "../../utils/date";
import { getSessionData } from "../../utils/storage";

type Step = "recorb" | "extension" | "confirm";

function DownloadPage() {
  const sessionData = getSessionData();
  const [currentStep, setCurrentStep] = useState<Step>("recorb");
  const [hardwareSignature, setHardwareSignature] = useState<string>("");
  const [serialNumber, setSerialNumber] = useState<string>("");
  const [hwNumber, setHwNumber] = useState<string>("");
  const defaultAgencyEmail = sessionData.agencyEmail || "agencyemail@dipro.vn";
  const [notificationEmail, setNotificationEmail] =
    useState<string>(defaultAgencyEmail);
  const extensionBoxRef = useRef<ExtensionBoxDownloadRef>(null);
  const recorbBoxRef = useRef<RecorbBoxDownloadRef>(null);
  const shouldScrollToTopRef = useRef(false);
  // New state variables
  const [agentName, setAgentName] = useState<string>(
    sessionData.agentName || "Agent name",
  );
  const [agencyContactPerson, setAgencyContactPerson] = useState<string>(
    sessionData.agentPersonInCharge || "Agency Name",
  );
  const [agencyEmail, setAgencyEmail] = useState<string>(defaultAgencyEmail);
  const [clinicName, setClinicName] = useState<string>("");
  const [userContactName, setUserContactName] = useState<string>("");
  const [userContactEmail, setUserContactEmail] = useState<string>("");
  const [fileName, setFileName] = useState("");

  const [showResult, setShowResult] = useState(false);
  const [textNoti, setTextNoti] = useState<any>(null);
  const [uploadStatus, setUploadStatus] = useState<boolean>(false);

  const { id } = useParams();

  const { appDownloadDetail, loading: loadingAppDownloadDetail } =
    useAppDownloadDetail(Number(id));
  const { appDownloadDetailVersion, loading: loadingAppDownloadDetailVersion } =
    useAppDownloadDetailVersion(Number(id));
  const { submitting, submitExtensionForm } = useExtensionForm();

  const rawChanges = appDownloadDetailVersion?.items?.[0]?.changes || "";
  const changeItems = rawChanges
    .split(/\r?\n/)
    .map((item: string) => item.trim())
    .filter((item: string) => item.length > 0);

  const scrollPageToTop = () => {
    const scrollingElement = document.scrollingElement;

    window.scrollTo({ top: 0, behavior: "auto" });
    scrollingElement?.scrollTo({ top: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  useEffect(() => {
    if (!shouldScrollToTopRef.current) return;

    shouldScrollToTopRef.current = false;

    requestAnimationFrame(() => {
      scrollPageToTop();
    });
  }, [currentStep]);

  useEffect(() => {
    if (!showResult) return;

    requestAnimationFrame(() => {
      scrollPageToTop();
    });
  }, [showResult]);

  const handleNextStep = async () => {
    if (currentStep === "recorb") {
      const isValid = await recorbBoxRef.current?.validate();
      if (isValid) {
        const stateLicense = await api.post(
          `/api/licenses/serialNumber/actived`,
          { serialNumber },
        );
        if (stateLicense.data?.exists) {
          setShowResult(true);
          return;
        } else {
          shouldScrollToTopRef.current = true;
          setCurrentStep("extension");
        }
      } else {
        textNoti
          ? setTextNoti(textNoti)
          : setTextNoti("Please Upload Plugin Purchase Info File");
        setUploadStatus(true);
      }
    } else if (currentStep === "extension") {
      const isValid = await extensionBoxRef.current?.validate();
      if (isValid) {
        shouldScrollToTopRef.current = true;
        setCurrentStep("confirm");
      }
    }
  };

  const handleBackStep = (step: Step) => {
    shouldScrollToTopRef.current = true;
    setCurrentStep(step);
  };

  return (
    <LayoutDownload className="w-full text-left text-[#2C3A4B]">
      {showResult ? (
        <RecorbDownloadResult />
      ) : (
        <div className="flex flex-col justify-center items-center w-full">
          <div className="m-auto mb-5 w-fit">
            <img
              src={dicomLogo}
              alt="Dicom Plugin Logo"
              className="w-[179px]"
            />
          </div>
          <div className="flex flex-col gap-6 w-full">
            <div>
              <div className="px-4 w-full">
                <button className="h-[30px] bg-[#0077C8] text-white px-1 text-sm mb-2">
                  Application Information
                </button>
                {loadingAppDownloadDetail ? (
                  <Skeleton paragraph={{ rows: 1 }} active />
                ) : (
                  <p className="mb-4 text-sm">
                    {appDownloadDetail?.appInfo}
                  </p>
                )}
              </div>
              <hr />
              <div className="px-4 mt-4 w-full">
                {loadingAppDownloadDetail || loadingAppDownloadDetailVersion ? (
                  <>
                    <div className="h-[30px] w-[220px] mb-2 rounded bg-[#E5E7EB] animate-pulse" />
                    <div className="flex gap-2">
                      {[0, 1].map((col) => (
                        <div key={col} className="flex-1 space-y-2 text-sm">
                          {[0, 1, 2, 3].map((row) => (
                            <div
                              key={row}
                              className="h-[18px] w-full rounded bg-[#E5E7EB] animate-pulse"
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <button className="h-[30px] bg-[#0077C8] text-white px-1 text-sm mb-2 font-normal">
                      Version {appDownloadDetailVersion?.items?.[0]?.version}（
                      {formatDate(
                        appDownloadDetailVersion?.items?.[0]?.releaseDate || "",
                        DATE_FORMAT.SLASH_DATE,
                      )}
                      ）
                    </button>

                    <div className="flex gap-2">
                      {[0, 1].map((col) => (
                        <div key={col} className="flex-1 text-sm">
                          {changeItems
                            .filter(
                              (_: string, index: number) => index % 2 === col,
                            )
                            .map((item: string, i: number) => (
                              <p key={i} className="mb-2 last:mb-0">
                                ・{item}
                              </p>
                            ))}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div>
              {currentStep === "confirm" ? (
                <div className="flex items-center h-[56px] gap-[10px] mb-4">
                  <img src={inforIcon} alt="infor" />
                  <div>
                    <p className="text-[#333333] font-bold mb-0 leading-7">
                      Please proceed with your purchase using the information
                      below.{" "}
                    </p>
                    <p className="text-[#333333] font-bold mb-0 leading-7">
                      If you agree, please press the Download button. If you
                      need to make changes, press the Back button.{" "}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center h-8 gap-[10px] mb-4">
                  <img src={inforIcon} alt="infor" />
                  <p className="text-[#333333] font-bold mb-0">
                    Please enter the following information to download the app.
                  </p>
                </div>
              )}
              <div>
                {currentStep === "recorb" && (
                  <RecorbBoxDownload
                    ref={recorbBoxRef}
                    serialNumber={serialNumber}
                    setSerialNumber={setSerialNumber}
                    hardwareSignature={hardwareSignature}
                    setHardwareSignature={setHardwareSignature}
                    setHwNumber={setHwNumber}
                    fileName={fileName}
                    setFileName={setFileName}
                    textNoti={textNoti}
                    setTextNoti={setTextNoti}
                    uploadStatus={uploadStatus}
                    setUploadStatus={setUploadStatus}
                  />
                )}
                {currentStep === "extension" && (
                  <ExtensionBoxDownload
                    serialNumber={serialNumber}
                    hwNumber={hwNumber}
                    ref={extensionBoxRef}
                    notificationEmail={notificationEmail}
                    onNotificationEmailChange={setNotificationEmail}
                    agentName={agentName}
                    onAgentNameChange={setAgentName}
                    agencyContactPerson={agencyContactPerson}
                    onAgencyContactPersonChange={setAgencyContactPerson}
                    agencyEmail={agencyEmail}
                    onAgencyEmailChange={setAgencyEmail}
                    clinicName={clinicName}
                    setClinicName={setClinicName}
                    userContactName={userContactName}
                    onUserContactNameChange={setUserContactName}
                    userContactEmail={userContactEmail}
                    onUserContactEmailChange={setUserContactEmail}
                    fileName={fileName}
                  />
                )}
                {currentStep === "confirm" && (
                  <ExtensionPluginConfirm
                    serialNumber={serialNumber}
                    hwNumber={hwNumber}
                    fileName={fileName}
                    notificationEmail={notificationEmail}
                    agentName={agentName}
                    agencyContactPerson={agencyContactPerson}
                    agencyEmail={agencyEmail}
                    clinicName={clinicName}
                    userContactName={userContactName}
                    userContactEmail={userContactEmail}
                  />
                )}
              </div>
            </div>
            <div className="m-auto w-fit mb-[154px] flex gap-4">
              {currentStep === "confirm" && (
                <button
                  onClick={() => {
                    handleBackStep("extension");
                  }}
                  className="h-[50px] bg-white border-[#6D7580] border text-[#6D7580] w-[282px] text-xl font-medium rounded-lg "
                >
                  Back
                </button>
              )}
              {currentStep === "confirm" ? (
                <button
                  onClick={async () => {
                    await submitExtensionForm({
                      serialNumber,
                      hardwareSignature,
                      agentName,
                      agencyContactPerson,
                      agencyEmail,
                      clinicName,
                      userContactName: userContactName || undefined,
                      userContactEmail: userContactEmail || undefined,
                      notificationEmail,
                      versionId: appDownloadDetailVersion?.items?.[0]?.id,
                    });
                    setShowResult(true);
                  }}
                  disabled={submitting}
                  className="h-[50px] bg-[#0077C8] text-white w-[282px] text-xl font-medium rounded-lg shadow-[inset_0_-2px_8.6px_0_#00000040,inset_0_3px_4px_0_#FFFFFF40] flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <img src={downloadIcon} alt="download" className="w-5 h-5" />
                  <span>Download</span>
                </button>
              ) : (
                <>
                  {currentStep === "extension" && (
                    <button
                      onClick={() => {
                        handleBackStep("recorb");
                        // setSerialNumber("");
                      }}
                      className="h-[50px] bg-white border-[#6D7580] border text-[#6D7580] w-[282px] text-xl font-medium rounded-lg "
                    >
                      Back
                    </button>
                  )}
                  <button
                    onClick={handleNextStep}
                    className="base-button h-[50px] bg-[#0077C8] text-white w-[282px] !text-xl !font-medium rounded-lg shadow-[inset_0_-2px_8.6px_0_#00000040,inset_0_3px_4px_0_#FFFFFF40]"
                  >
                    Next
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </LayoutDownload>
  );
}

export default DownloadPage;
